import axios from 'axios';
import { store } from '../store'
// import { refresh } from "../Redux/UserAuthSlice/UserAuthSlice"

const RATE_LIMIT_RETRY_DELAY_MS = 3000;
const MAX_RATE_LIMIT_RETRIES = 3;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getRateLimitRetryDelay = (response) => {
    const retryAfter = response?.headers?.['retry-after'];
    if (!retryAfter) {
        return RATE_LIMIT_RETRY_DELAY_MS;
    }

    const retryAfterSeconds = parseInt(retryAfter, 10);
    if (!Number.isNaN(retryAfterSeconds)) {
        return retryAfterSeconds * 1000;
    }

    const retryAfterDate = Date.parse(retryAfter);
    if (!Number.isNaN(retryAfterDate)) {
        return Math.max(0, retryAfterDate - Date.now());
    }

    return RATE_LIMIT_RETRY_DELAY_MS;
};

const isNetworkOrCorsError = (error) => {
    if (error?.response) {
        return false;
    }

    const message = (error?.message || '').toLowerCase();
    const code = error?.code || '';

    return (
        code === 'ERR_NETWORK' ||
        message === 'network error' ||
        message.includes('cors') ||
        message.includes('network')
    );
};

const createEmptyDataModel = (url = '') => {
    if (/\/FindPatientCollections/i.test(url)) {
        return {
            totalCharge: 0,
            insurancePayment: 0,
            patientBalance: 0,
            patientPayment: 0,
        };
    }

    if (/\/DropDown\/|GetPatientPlan|FindPatientDocuments|FindTag\b/i.test(url)) {
        return [];
    }

    if (/Search|Report|Grid|pageNo|perPage|totalCount/i.test(url)) {
        return {
            data: [],
            totalCount: 0,
            pageNo: 1,
            perPage: 10,
        };
    }

    return [];
};

const createEmptySuccessResponse = (config) => ({
    data: {
        dataModel:
            config?.emptyDataModel !== undefined
                ? config.emptyDataModel
                : createEmptyDataModel(config?.url || ''),
        responseMessage: '',
    },
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
    request: {},
});

const api = axios.create({
    withCredentials: false,
    headers: {
        Accept: "*/*",
        "Content-Type": "application/json",
    },
});

// Add an interceptor to include the token in the headers of the request
api.interceptors.request.use(
    (config) => {
        let token = store.getState().userAuth.userinfo.token
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add an interceptor to refresh the token if it has expired
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 429 && originalRequest) {
            const retryCount = originalRequest._rateLimitRetryCount || 0;
            if (retryCount < MAX_RATE_LIMIT_RETRIES) {
                originalRequest._rateLimitRetryCount = retryCount + 1;
                const delayMs = getRateLimitRetryDelay(error.response);
                await sleep(delayMs);
                return api(originalRequest);
            }
        }

        if (
            isNetworkOrCorsError(error) &&
            originalRequest &&
            !originalRequest.rejectOnNetworkError
        ) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn(
                    '[api] Network/CORS error — returning empty dataModel for',
                    originalRequest.url
                );
            }
            return createEmptySuccessResponse(originalRequest);
        }

        if (error.response?.status === 401 && originalRequest && !originalRequest.isRetry) {
            originalRequest.isRetry = true;
            try {
                // let refreshToken = store.getState().userAuth.userinfo.refreshToken
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/refresh`,);
                const newTokens = response.data;
                // store.dispatch(refresh({ newTokens }));
                originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
                return api(originalRequest);
            } catch (err) {
                return Promise.reject(err);
            }
        }
        return Promise.reject(error);
    }
);

export default api;
